import api from './axiosConfig';
import { Benefit } from './benefits'
import { BusinessCategory } from './businessCategories'
import { PaymentProvider } from './paymentProviders'
import { ExternalLinks } from './externalLinks'
import { Image } from './common'
import { PaginatedResponse } from "./transactions";

export interface Business {
  id: string;
  name: string;
  merchantContractNumbers: string[];
  businessCategories: BusinessCategory[];
  description: string;
  location: { lon: number; lat: number } | null;
  logo: Image;
  mainImage: Image;
  images: Image[];
  benefits: Benefit[];
  paymentProviders: PaymentProvider[];
  externalLinks: ExternalLinks;
  billingIdentificationNumber: string;
  alternativeMerchantNames: string[];
  hasPendingChanges: boolean | null;
}

export interface CreateBusinessInput {
  name: string;
  merchantContractNumbers: string[];
  businessCategoryIds: string[];
  description: string;
  location: { lon: number; lat: number } | null;
  logoId: string;
  mainImageId: string;
  imageIds: string[];
  benefitIds: string[];
  paymentProviderIds: string[];
  externalLinksId: string;
  billingIdentificationNumber: string;
  alternativeMerchantNames: string[];
}

export const createBusiness = async (
  input: CreateBusinessInput
): Promise<void> => {
  try {
    const response = await api.post(
      "/business-account/create-or-update-business",
      { CreateOrUpdateBusinessInput: input }
    );
    return response.data;
  } catch (error) {
    console.error("Creating business error:", error);
    throw error;
  }
};

export const updateBusiness = async (
  id: string,
  input: CreateBusinessInput
): Promise<void> => {
  try {
    const response = await api.post(
      "/business-account/create-or-update-business",
      { CreateOrUpdateBusinessInput: { ...input, Id: id } }
    );
    return response.data;
  } catch (error) {
    console.error("Creating business error:", error);
    throw error;
  }
};

export const getBusiness = async (id: string): Promise<Business> => {
  try {
    const response = await api.post(`/business-account/get-business`, {
      businessId: id,
    });
    return response.data;
  } catch (error) {
    console.error("Creating business error:", error);
    throw error;
  }
};

export const listBusinesses = async (): Promise<Business[]> => {
  try {
    const response = await api.post("/business-account/list-businesses", {});
    return response.data;
  } catch (error) {
    console.error("Creating business error:", error);
    throw error;
  }
};

export const listBusinessesForAdmin = async (
  companyId: string
): Promise<Business[]> => {
  try {
    const response = await api.post(
      "/business-account/list-businesses-for-admin",
      { companyId }
    );
    return response.data;
  } catch (error) {
    console.error("Listing businesses for admin error:", error);
    throw error;
  }
};

export interface BusinessName {
  /** Contentful entry id — what the backend expects as `businessContentfulId`. */
  id: string;
  name: string;
  /**
   * Contentful asset URLs, protocol-relative (`//images.ctfassets.net/...`) as Contentful
   * returns them. Present so a caller can show the operator which image a notification will
   * actually carry instead of making them guess; use `toAbsoluteImageUrl` before rendering.
   */
  logoUrl?: string | null;
  mainImageUrl?: string | null;
}

/** Contentful returns protocol-relative URLs; an `<img>` src needs a scheme. */
export const toAbsoluteImageUrl = (
  url: string | null | undefined
): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.startsWith("http://")) return `https://${url.slice("http://".length)}`;
  return url;
};

export interface LinkablePartner {
  id: string;
  /** Verbatim from Contentful — leading whitespace is what separates duplicate names. */
  name: string;
  hasValidBenefit: boolean;
}

/**
 * Every published business, including partners whose cashback benefit has lapsed.
 *
 * Not `listBusinessNames`: that one is the customer-facing list and drops lapsed partners. A
 * campaign is linked after the fact, so the partner it promoted may no longer be live — on
 * production the Zalando entry that took 176 profile opens was filtered out of that list while the
 * one that took a single open was offered.
 */
export const listLinkablePartners = async (): Promise<LinkablePartner[]> => {
  try {
    const response = await api.post<LinkablePartner[]>("/business/list-linkable-partners", {});
    return response.data ?? [];
  } catch (error) {
    console.error("Listing linkable partners error:", error);
    throw error;
  }
};

export const listBusinessNames = async (): Promise<BusinessName[]> => {
  try {
    // /business/list-businesses is authorized via AllowRule (accessible to admin
    // accounts), unlike /business/list-businesses-names which requires the
    // IdentityRole.Admin claim that dashboard admins don't carry.
    const response = await api.post("/business/list-businesses", {
      filterCriteria: {},
      searchCriteria: {},
    });
    const businesses: Array<{
      id?: string | null;
      name?: string | null;
      logoUrl?: string | null;
      mainImageUrl?: string | null;
    }> = response.data || [];
    return businesses
      .filter((business) => !!business.id)
      .map((business) => ({
        id: business.id as string,
        name: business.name ?? "",
        logoUrl: business.logoUrl ?? null,
        mainImageUrl: business.mainImageUrl ?? null,
      }));
  } catch (error) {
    console.error("Listing business names error:", error);
    throw error;
  }
};

// Top Merchants API
export interface TopMerchant {
  merchantName: string;
  totalTransactionAmount: number;
  totalAssignedCashback: number;
  transactionCount: number;
}

export enum MerchantSortBy {
  TotalAssignedCashback = "TotalAssignedCashback",
  TotalTransactionAmount = "TotalTransactionAmount",
  TransactionCount = "TransactionCount",
}

export interface ListTopMerchantsRequest {
  pagination: {
    pageNumber: number;
    pageSize: number;
  };
  companyId?: string;
  from?: string; // ISO 8601 format
  to?: string; // ISO 8601 format
  sortBy?: MerchantSortBy;
}

export interface TopMerchantsResponse {
  data: PaginatedResponse<TopMerchant>;
}

export const listTopMerchantsForAdmin = async (
  request: ListTopMerchantsRequest
): Promise<TopMerchantsResponse> => {
  try {
    const response = await api.post(
      "/business-account/list-top-merchants-for-admin",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Listing top merchants for admin error:", error);
    throw error;
  }
};

// Real-time snapshot of platform-wide point balances (values already in points = DKK).
export interface OisterBonusPointSnapshot {
  totalPointBalance: number;
  usersWithPositiveBalance: number;
  usersWithBalanceAbove500: number;
}

export interface OisterBonusPointOverview extends OisterBonusPointSnapshot {
  // Snapshot of balances just before the most recent bill-run (downsizing).
  // null until at least one bill run exists in the DB.
  beforeLastDownsizing?:
    | (OisterBonusPointSnapshot & { asOf: string })
    | null;
}

export const getOisterBonusPointOverview = async (
  asOf?: string // ISO 8601 instant; omitted => current snapshot
): Promise<OisterBonusPointOverview> => {
  try {
    const response = await api.post(
      "/business-account/get-oister-bonus-point-overview-for-admin",
      asOf ? { asOf } : {}
    );
    return response.data;
  } catch (error) {
    console.error("Getting OiSTER bonus point overview error:", error);
    throw error;
  }
};

// Zero-result Searches API
export interface ZeroResultSearch {
  query: string;
  count: number;
}

export interface ListZeroResultSearchesRequest {
  limit?: number;
}

export interface ZeroResultSearchesResponse {
  data: ZeroResultSearch[];
}

export const listZeroResultSearchesForAdmin = async (
  request: ListZeroResultSearchesRequest
): Promise<ZeroResultSearchesResponse> => {
  try {
    const response = await api.post(
      "/business-account/list-zero-result-searches-for-admin",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Listing zero-result searches for admin error:", error);
    throw error;
  }
};

// Search → Click Log API
export interface SearchClickLogEntry {
  userId: string;
  query: string;
  businessId: string;
  clickedAt: string; // ISO 8601
  eventType: string;
}

export interface ListSearchClickLogRequest {
  from: string; // ISO 8601
  to: string; // ISO 8601
  query?: string;
  businessId?: string;
  userId?: string;
  page?: number;
  perPage?: number;
}

export interface SearchClickLogResponse {
  data: SearchClickLogEntry[];
  totalFound: number;
  page: number;
  perPage: number;
}

export const listSearchToClickLogForAdmin = async (
  request: ListSearchClickLogRequest
): Promise<SearchClickLogResponse> => {
  try {
    const response = await api.post(
      "/business-account/list-search-to-click-log-for-admin",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Listing search-to-click log for admin error:", error);
    throw error;
  }
};

// Conversion Funnel API
export type FunnelGroupBy = "Query" | "Business" | "Partner";

export interface FunnelSteps {
  clicks: number;
  uniqueClickers: number;
  conversions: number;
  revenue: number;
  conversionRate: number;
}

export interface FunnelBreakdownRow {
  key: string;
  clicks: number;
  conversions: number;
  revenue: number;
  conversionRate: number;
}

export interface ListConversionFunnelRequest {
  from: string; // ISO 8601
  to: string; // ISO 8601
  businessId?: string;
  attributionWindowMinutes?: number;
  groupBy?: FunnelGroupBy;
}

export interface ConversionFunnelResponse {
  steps: FunnelSteps;
  breakdown: FunnelBreakdownRow[];
}

export const listConversionFunnelForAdmin = async (
  request: ListConversionFunnelRequest
): Promise<ConversionFunnelResponse> => {
  try {
    const response = await api.post(
      "/business-account/list-conversion-funnel-for-admin",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Listing conversion funnel for admin error:", error);
    throw error;
  }
};